import React, { useEffect, useState } from 'react';
import { fetchGitHubStats } from '../services/githubAPI';
import { fetchLeetCodeStats } from '../services/leetcodeAPI';
import { fetchCodeforcesStats } from '../services/codeforcesAPI';
import { db } from '../firebase/firebaseConfig';
import { ref, update, increment } from 'firebase/database';
import './StatsCard.css';

function StatsCard({ platform, username, accentColor, iconClass }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!username) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        let result;
        if (platform === 'GitHub') result = await fetchGitHubStats(username);
        if (platform === 'LeetCode') result = await fetchLeetCodeStats(username);
        if (platform === 'Codeforces') result = await fetchCodeforcesStats(username);
        
        setData(result);
        
        // Track API usage globally
        try {
          await update(ref(db, 'stats/api_usage'), {
            [platform]: increment(1),
            total: increment(1)
          });
        } catch (logErr) {
          console.error("Failed to log API stat", logErr);
        }
      } catch (err) {
        setError(`Failed to load ${platform} stats`);
      }
      setLoading(false);
    };

    if (platform !== 'GeeksforGeeks') {
        fetchData();
    } else {
        setLoading(false); // GFG relies mostly on manual links right now
    }
  }, [platform, username]);

  // CSS Var injection for borders
  const cardStyle = { '--card-accent': accentColor };

  if (!username) {
    return (
      <div className="glass-card stat-card" style={cardStyle}>
        <div className="stat-header">
          <i className={iconClass} style={{color: accentColor}}></i>
          <h3>{platform}</h3>
        </div>
        <div className="stat-empty">Username not configured</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="glass-card stat-card" style={cardStyle}>
        <div className="stat-header">
          <i className={iconClass} style={{color: accentColor}}></i>
          <h3>{platform}</h3>
        </div>
        <div className="stat-loading"><div className="loader"></div></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="glass-card stat-card" style={cardStyle}>
        <div className="stat-header">
          <i className={iconClass} style={{color: accentColor}}></i>
          <h3>{platform}</h3>
        </div>
        <div className="stat-error">{error}</div>
      </div>
    );
  }

  // PLATFORM RENDERERS
  const renderGitHub = () => (
    <>
      <div className="stat-avatar-row">
        {data.avatar && <img src={data.avatar} alt="avatar" className="stat-avatar" />}
        <span className="stat-username">@{data.username}</span>
      </div>
      <div className="stat-row"><span>Public Repos</span> <strong>{data.publicRepos}</strong></div>
      <div className="stat-row"><span>Followers</span> <strong>{data.followers}</strong></div>
      <div className="stat-row"><span>Following</span> <strong>{data.following}</strong></div>
      <a href={data.url} target="_blank" rel="noreferrer" className="stat-link-btn">View Profile</a>
    </>
  );

  const renderLeetCode = () => (
    <>
      <div className="stat-avatar-row">
        <span className="stat-username">@{data.username}</span>
      </div>
      <div className="stat-row"><span>Rank</span> <strong>{data.ranking?.toLocaleString() || 'N/A'}</strong></div>
      <div className="stat-row"><span>Total Solved</span> <strong>{data.totalSolved}</strong></div>
      <div className="stat-row"><span style={{color: '#00b8a3'}}>Easy</span> <strong>{data.easySolved}</strong></div>
      <div className="stat-row"><span style={{color: '#ffc01e'}}>Medium</span> <strong>{data.mediumSolved}</strong></div>
      <div className="stat-row"><span style={{color: '#ff375f'}}>Hard</span> <strong>{data.hardSolved}</strong></div>
      <a href={data.url} target="_blank" rel="noreferrer" className="stat-link-btn">View Profile</a>
    </>
  );

  const renderCodeforces = () => (
    <>
      <div className="stat-avatar-row">
        {data.avatar && <img src={data.avatar} alt="avatar" className="stat-avatar" />}
        <span className="stat-username">@{data.username}</span>
      </div>
      <div className="stat-row"><span>Rank</span> <strong>{data.rank}</strong></div>
      <div className="stat-row"><span>Current Rating</span> <strong>{data.rating}</strong></div>
      <div className="stat-row"><span>Max Rating</span> <strong>{data.maxRating}</strong></div>
      <div className="stat-row"><span>Solved</span> <strong>{data.problemsSolved}</strong></div>
      <a href={data.url} target="_blank" rel="noreferrer" className="stat-link-btn">View Profile</a>
    </>
  );

  const renderGFG = () => (
    <>
      <div className="stat-avatar-row">
        <span className="stat-username">@{username}</span>
      </div>
      <div className="stat-empty" style={{margin: '15px 0'}}>
        No public API available for GFG. Use link to view.
      </div>
      <a href={`https://auth.geeksforgeeks.org/user/${username}/`} target="_blank" rel="noreferrer" className="stat-link-btn">View Profile</a>
    </>
  );

  return (
    <div className="glass-card stat-card" style={cardStyle}>
      <div className="stat-header">
        <i className={iconClass} style={{color: accentColor}}></i>
        <h3>{platform}</h3>
      </div>
      <div className="stat-content">
        {platform === 'GitHub' && data && renderGitHub()}
        {platform === 'LeetCode' && data && renderLeetCode()}
        {platform === 'Codeforces' && data && renderCodeforces()}
        {platform === 'GeeksforGeeks' && renderGFG()}
      </div>
    </div>
  );
}

export default StatsCard;
